<script setup lang="ts">
import { ref } from 'vue';
import { toast } from 'vue-sonner';
import { EllipsisHorizontalIcon } from '@heroicons/vue/24/outline';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePublishSharedSavedView } from '@/composables/api/useSharedSavedViewsApi';
import { useMountedWhileOpen } from '@/composables/useMountedWhileOpen';
import { useSavedViewRowActions } from '@/layouts/frame/savedViewRowActions';
import { buildShareUrl, copyShareToClipboard } from '@/utils/savedViewSharing';
import type { SavedViewRecord } from '@/utils/savedViews';

/**
 * The "…" on a saved view in the sidebar: Save changes (on the open view, while
 * it has some), Rename, Icon and colour, Share and Delete. The toolbar has no saved-view
 * controls any more (SAU-437).
 *
 * A sidebar row can belong to another entity than the list on screen, for
 * example an Accounts view while Zones is open, so every action names the
 * view's own entity rather than the current list's.
 *
 * The row holds only the "…" button. Its menu is in the page only while it is
 * open or fading out. Rename, "Icon and colour" and Delete open the sidebar's
 * one set of dialogs, in SavedViewRowDialogs. So a row has no Teleport while
 * its menu is closed, and changing area removes rows without removing any
 * Teleport (see useMountedWhileOpen).
 */
const props = withDefaults(defineProps<{ view: SavedViewRecord; canSaveChanges?: boolean }>(), { canSaveChanges: false });
const emit = defineEmits<{ 'save-changes': [] }>();

const publishMutation = usePublishSharedSavedView();
const { openDialog } = useSavedViewRowActions();

const menuOpen = ref(false);
const { mounted: menuMounted, onCloseFinished: onMenuClosed } = useMountedWhileOpen(menuOpen);

async function share() {
    // Started inside the click, before the publish is awaited, because Safari
    // refuses a clipboard write that begins after a network wait.
    const published = publishMutation.mutateAsync({ entity: props.view.entity, savedViewId: props.view.id });
    const url = published.then((row) => buildShareUrl(window.location.origin, row.share_key));
    const copied = copyShareToClipboard(props.view.name, url);
    copied.catch(() => {});
    try {
        await published;
    } catch {
        return;
    }
    try {
        await copied;
        toast.success('Share link copied — paste it into Slack to share.');
    } catch {
        toast.error(`Shared, but the link could not be copied. It is: ${await url}`);
    }
}
</script>

<template>
  <DropdownMenu v-model:open="menuOpen">
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        :aria-label="`Actions for ${view.name}`"
        data-testid="saved-view-row-menu"
        class="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 hover:bg-background hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
        @click.stop.prevent
      >
        <EllipsisHorizontalIcon class="size-4" aria-hidden="true" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent v-if="menuMounted" align="start" side="right" class="min-w-40" @close-auto-focus="onMenuClosed">
      <template v-if="canSaveChanges">
        <DropdownMenuItem data-testid="saved-view-row-save" @select="emit('save-changes')">Save changes</DropdownMenuItem>
        <DropdownMenuSeparator />
      </template>
      <DropdownMenuItem data-testid="saved-view-row-rename" @select="openDialog('rename', view)">Rename</DropdownMenuItem>
      <DropdownMenuItem data-testid="saved-view-row-appearance" @select="openDialog('appearance', view)">Icon and colour…</DropdownMenuItem>
      <DropdownMenuItem data-testid="saved-view-row-share" @select="share">Share</DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem data-testid="saved-view-row-delete" class="text-destructive-foreground" @select="openDialog('delete', view)">Delete</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
